import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import clsx from "clsx";
import {
  AlertTriangle, ArrowLeft, CheckCircle2, ChevronLeft, ChevronRight, History, Pencil, RotateCcw,
} from "lucide-react";
import { isDemoWorkspace,
  api, type PageState, type RedoEstimate, type ReviewData, type ReviewItem, type ReviewPage,
  type VersionHistoryEntry,
} from "../api";
import { Modal, useConfirm } from "../components/Modal";
import { Alert } from "../components/Page";

/** Review screen: the original PDF page beside the converted page, the
 *  differences on that page and the fixes for them. Only flagged pages need
 *  attention; the navigator shows those by default, even on a 386-page
 *  document. */

const KIND_LABEL: Record<ReviewItem["kind"], string> = {
  value: "Changed value",
  misread: "Misread word",
  fact: "Fact check",
  pdf_typo_fixed: "PDF typo corrected",
  missing: "Missing text",
  extra: "Extra text",
  picture_text: "Text from a picture",
  added_label: "Added heading",
  wording: "Wording changed",
  dropped_sentence: "Sentence left out",
  invented_sentence: "Sentence not in the PDF",
  missing_picture: "Picture left out",
};

/** One-click reasons for "Mark OK" — the reviewer can still edit them. */
const NOTE_PRESETS: Record<ReviewItem["kind"], string[]> = {
  value: ["Checked against the PDF image — the converted value is right"],
  misread: ["The PDF has a typo; the corrected spelling is right", "Checked against the PDF image — the converted word is right"],
  fact: ["Checked against the PDF image — the converted value is right", "Shown elsewhere in the document"],
  pdf_typo_fixed: ["The PDF has a typo; the corrected spelling is right"],
  missing: ["Decorative or repeated text, not needed", "Text is in the picture description"],
  extra: ["Description the converter added for accessibility"],
  picture_text: ["Text read from a picture in the PDF"],
  added_label: ["Header added so the table is accessible"],
  wording: ["Checked against the PDF image — the converted wording is right",
    "The PDF has a typo; the corrected spelling is right"],
  dropped_sentence: ["The sentence is elsewhere in the document", "Decorative or repeated text, not needed"],
  invented_sentence: ["Description the converter added for accessibility"],
  missing_picture: ["The picture is described elsewhere in this day or cell", "Decorative picture, no description needed"],
};

const STATE_LABEL: Record<PageState, string> = {
  flagged: "needs a decision",
  warning: "has warnings",
  fixed: "fixed",
  unchecked: "no PDF text to compare",
  clean: "no differences",
};

const STATE_CLS: Record<PageState, string> = {
  flagged: "bg-rose-100 text-rose-800 ring-1 ring-rose-300",
  warning: "bg-amber-50 text-amber-900 ring-1 ring-amber-200",
  fixed: "bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200",
  unchecked: "bg-slate-100 text-slate-600 ring-1 ring-slate-200",
  clean: "bg-white text-slate-500 ring-1 ring-slate-200",
};

/** "3, 7-9" → [3, 7, 8, 9] */
function parsePages(text: string): number[] {
  const out = new Set<number>();
  for (const part of text.split(/[\s,]+/).filter(Boolean)) {
    const m = part.match(/^(\d+)(?:-(\d+))?$/);
    if (!m) continue;
    const a = Number(m[1]), b = m[2] ? Number(m[2]) : a;
    for (let n = Math.min(a, b); n <= Math.max(a, b) && out.size < 500; n++) out.add(n);
  }
  return [...out].sort((x, y) => x - y);
}

function previewDoc(styles: string, body: string): string {
  return `<!doctype html><html><head><meta charset="utf-8"><base target="_blank">
<style>body{margin:0;padding:16px;font-family:system-ui,sans-serif}mark{background:#fde68a;outline:2px solid #d97706}</style>
</head><body>${styles}<main class="sdoc-doc">${body}</main></body></html>`;
}

export function ReviewPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<ReviewData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [current, setCurrent] = useState<number | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [redoOpen, setRedoOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [confirm, confirmNode] = useConfirm();

  const load = useCallback(async () => {
    try {
      const d = await api.getReview(id);
      setData(d);
      setError(null);
      setCurrent((cur) => {
        if (cur != null && d.pages.some((p) => p.page === cur)) return cur;
        const first = d.pages.find((p) => p.state === "flagged") ?? d.pages.find((p) => p.state === "warning");
        return first?.page ?? d.pages[0]?.page ?? null;
      });
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  }, [id]);
  useEffect(() => { setData(null); load(); }, [load]);

  // A page redo runs in the background: poll until the new version is done.
  const running = data?.conversion.status === "running" || data?.conversion.status === "queued";
  useEffect(() => {
    if (!running) return;
    const t = window.setInterval(load, 4000);
    return () => window.clearInterval(t);
  }, [running, load]);

  /** After a fix the server may answer with a new version: follow it. */
  function applyResult(d: ReviewData, message: string) {
    setData(d);
    if (d.conversion.id !== id) {
      setNotice(`${message} The live version was not changed — this is a new version (${d.conversion.version}) waiting for approval.`);
      navigate(`/workbench/${d.conversion.id}/review`, { replace: true });
    } else {
      setNotice(message);
    }
  }

  const pages = data?.pages ?? [];
  const attention = useMemo(() => pages.filter((p) => p.state !== "clean"), [pages]);
  const listed = showAll ? pages : attention;
  const docItems = useMemo(() => (data?.items ?? []).filter((i) => i.page == null), [data]);
  const flaggedPages = useMemo(() => pages.filter((p) => p.state === "flagged").map((p) => p.page), [pages]);

  function step(dir: 1 | -1) {
    const seq = (showAll ? pages : attention).map((p) => p.page);
    if (!seq.length || current == null) return;
    const i = seq.indexOf(current);
    const next = i < 0 ? seq[0] : seq[Math.min(seq.length - 1, Math.max(0, i + dir))];
    setCurrent(next);
    setEditing(false);
  }

  async function approve() {
    if (!data) return;
    const ok = await confirm({
      title: "Publish this version?",
      message: "Visitors will see this version on the site. Every difference from the PDF has been fixed or accepted with a note.",
      confirmLabel: "Approve and publish",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.approveConversion(data.conversion.id);
      await load();
      setNotice("Published. The Ask feature is being re-indexed from this version.");
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }

  if (error && !data) {
    return (
      <div className="space-y-4">
        <Link to="/workbench" className="btn-ghost"><ArrowLeft className="h-4 w-4" /> Workbench</Link>
        <div role="alert" className="card card-body text-rose-700">{error}</div>
      </div>
    );
  }
  if (!data) return <div className="text-slate-500" role="status">Loading review…</div>;

  const c = data.conversion;
  const name = c.filename ? c.filename.replace(/\.pdf$/i, "") : "Document";

  return (
    <div className="space-y-4">
      {confirmNode}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <Link to="/workbench" className="btn-ghost -ml-3"><ArrowLeft className="h-4 w-4" /> Workbench</Link>
          <h1 className="text-xl sm:text-2xl font-semibold text-ink break-words line-clamp-2 sm:line-clamp-1" title={name}>Review: {name}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-600">
            <span>Version {c.version} of {c.version_count}</span>
            {c.published ? <span className="pill-succeeded">live</span>
              : running ? <span className="pill-running">pages being read again…</span>
              : <span className="pill-review">waiting for approval</span>}
            {!c.published && c.live_version != null && (
              <span className="text-emerald-700">Visitors see version {c.live_version}</span>
            )}
            {c.change_summary && <span className="text-slate-500">· {c.change_summary}</span>}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button className="btn-secondary" onClick={() => setHistoryOpen(true)}>
            <History className="h-4 w-4" aria-hidden="true" /> Version history
          </button>
          {data.can_edit && !isDemoWorkspace() && (
            <button className="btn-secondary" onClick={() => setRedoOpen(true)} disabled={busy || running}>
              <RotateCcw className="h-4 w-4" aria-hidden="true" /> Redo pages…
            </button>
          )}
          {!c.published && (
            <button
              className="btn-primary"
              onClick={approve}
              disabled={!data.can_approve || busy}
              aria-describedby="approve-why"
            >
              <CheckCircle2 className="h-4 w-4" aria-hidden="true" /> Approve and publish
            </button>
          )}
        </div>
      </div>

      <div id="approve-why" className="text-sm" aria-live="polite">
        {c.published ? (
          <span className="text-emerald-800">This version is live. Changes you make create a new version for approval.</span>
        ) : !c.is_latest ? (
          <span className="text-slate-600">A newer version exists — this one is read only.</span>
        ) : !data.items_available ? (
          <span className="text-amber-800">Differences can't be listed (the original PDF or HTML is no longer stored).</span>
        ) : data.open_blocking ? (
          <span className="text-rose-800 font-medium">
            <AlertTriangle className="inline h-4 w-4 mr-1" aria-hidden="true" />
            {data.open_blocking} difference{data.open_blocking === 1 ? "" : "s"} from the PDF need{data.open_blocking === 1 ? "s" : ""} a
            decision before this can be approved
            {flaggedPages.length > 0 && <> (page{flaggedPages.length === 1 ? "" : "s"} {flaggedPages.slice(0, 12).join(", ")}{flaggedPages.length > 12 ? "…" : ""})</>}.
          </span>
        ) : (
          <span className="text-emerald-800">Nothing blocking remains{data.open_warnings ? ` — ${data.open_warnings} warning(s) to glance at` : ""}.</span>
        )}
      </div>

      {notice && <Alert tone="success" className="!mb-0" onDismiss={() => setNotice(null)}>{notice}</Alert>}
      {error && <Alert className="!mb-0">{error}</Alert>}

      <div className="grid grid-cols-1 lg:grid-cols-[220px_minmax(0,1fr)] gap-4 items-start">
        <nav className="card p-3 lg:sticky lg:top-0 max-h-60 lg:max-h-[80vh] overflow-y-auto" aria-label="Pages">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold text-ink">Pages</h2>
            <span className="text-xs text-slate-500">{attention.length} of {pages.length} to check</span>
          </div>
          <fieldset className="mb-2 text-xs flex gap-3">
            <legend className="sr-only">Show</legend>
            <label className="inline-flex items-center gap-1">
              <input type="radio" name="pagefilter" checked={!showAll} onChange={() => setShowAll(false)} /> Needs attention
            </label>
            <label className="inline-flex items-center gap-1">
              <input type="radio" name="pagefilter" checked={showAll} onChange={() => setShowAll(true)} /> All
            </label>
          </fieldset>
          {docItems.length > 0 && (
            <p className="mb-2 text-xs text-amber-900 bg-amber-50 rounded p-2">
              {docItems.filter((i) => i.status === "open").length} whole-document item(s) are listed under every page's items.
            </p>
          )}
          {listed.length === 0 ? (
            <p className="text-sm text-slate-500">No page has differences from the PDF.</p>
          ) : (
            <ul className="space-y-1">
              {listed.map((p) => (
                <li key={p.page}>
                  <button
                    type="button"
                    onClick={() => { setCurrent(p.page); setEditing(false); }}
                    aria-current={current === p.page ? "page" : undefined}
                    className={clsx(
                      "w-full flex items-center justify-between gap-2 rounded px-2 py-1 text-sm text-left",
                      STATE_CLS[p.state],
                      current === p.page && "outline outline-2 outline-brand-500",
                    )}
                  >
                    <span className="font-medium">Page {p.page}</span>
                    <span className="text-xs">
                      {p.state === "flagged" ? `${p.blocking} to decide`
                        : p.state === "warning" ? `${p.warnings} warning${p.warnings === 1 ? "" : "s"}`
                        : STATE_LABEL[p.state]}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </nav>

        {current != null ? (
          <PagePanel
            key={`${c.id}-${current}`}
            data={data}
            page={current}
            docItems={docItems}
            editing={editing}
            setEditing={setEditing}
            onPrev={() => step(-1)}
            onNext={() => step(1)}
            onResult={applyResult}
            onError={setError}
          />
        ) : (
          <div className="card card-body text-slate-500">This conversion has no pages to review.</div>
        )}
      </div>

      {redoOpen && (
        <RedoDialog
          data={data}
          initial={current != null ? [current] : []}
          flagged={flaggedPages}
          onClose={() => setRedoOpen(false)}
          onStarted={(newId) => {
            setRedoOpen(false);
            setNotice("Reading the pages again. This takes a minute or two; the page updates by itself.");
            navigate(`/workbench/${newId}/review`, { replace: true });
          }}
        />
      )}
      {historyOpen && <HistoryDialog id={c.id} onClose={() => setHistoryOpen(false)} />}
    </div>
  );
}

function PagePanel({
  data, page, docItems, editing, setEditing, onPrev, onNext, onResult, onError,
}: {
  data: ReviewData;
  page: number;
  docItems: ReviewItem[];
  editing: boolean;
  setEditing: (v: boolean) => void;
  onPrev: () => void;
  onNext: () => void;
  onResult: (d: ReviewData, message: string) => void;
  onError: (m: string) => void;
}) {
  const id = data.conversion.id;
  const [pageData, setPageData] = useState<ReviewPage | null>(null);
  const [img, setImg] = useState<string | null>(null);
  const [imgError, setImgError] = useState<string | null>(null);
  const summary = data.pages.find((p) => p.page === page);
  const items = useMemo(
    () => [...data.items.filter((i) => i.page === page), ...docItems],
    [data.items, page, docItems],
  );

  useEffect(() => {
    let alive = true;
    api.getReviewPage(id, page).then((p) => { if (alive) setPageData(p); })
      .catch((e) => onError(e instanceof Error ? e.message : String(e)));
    return () => { alive = false; };
    // Reload the page content whenever the items change (a fix was applied).
  }, [id, page, data.items, onError]);

  useEffect(() => {
    let url: string | null = null;
    let alive = true;
    api.pageImageUrl(id, page).then((u) => { url = u; if (alive) setImg(u); else URL.revokeObjectURL(u); })
      .catch((e) => alive && setImgError(e instanceof Error ? e.message : String(e)));
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [id, page]);

  return (
    <section className="space-y-4 min-w-0" aria-labelledby="page-heading">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="page-heading" className="text-lg font-semibold text-ink">
          Page {page}
          {summary && <span className={clsx("ml-2 pill", STATE_CLS[summary.state])}>{STATE_LABEL[summary.state]}</span>}
        </h2>
        <div className="flex gap-2">
          <button className="btn-ghost" onClick={onPrev}><ChevronLeft className="h-4 w-4" aria-hidden="true" /> Previous</button>
          <button className="btn-ghost" onClick={onNext}>Next <ChevronRight className="h-4 w-4" aria-hidden="true" /></button>
          {data.can_edit && !editing && (
            <button className="btn-secondary" onClick={() => setEditing(true)} disabled={!pageData}>
              <Pencil className="h-4 w-4" aria-hidden="true" /> Edit page
            </button>
          )}
        </div>
      </div>

      <ItemList data={data} items={items} onResult={onResult} onError={onError} />

      {editing && pageData ? (
        <PageEditor
          data={data}
          page={pageData}
          onCancel={() => setEditing(false)}
          onSaved={(d) => { setEditing(false); onResult(d, `Page ${page} saved.`); }}
          onError={onError}
        />
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <figure className="card p-2 m-0">
            <figcaption className="text-xs font-semibold text-slate-600 px-1 pb-2">Original PDF</figcaption>
            {img ? (
              <img src={img} alt={`Page ${page} of the original PDF`} className="w-full h-auto border border-slate-200" />
            ) : (
              <div className="h-96 flex items-center justify-center text-sm text-slate-500" role="status">
                {imgError ?? "Loading page image…"}
              </div>
            )}
          </figure>
          <div className="card p-2">
            <div className="text-xs font-semibold text-slate-600 px-1 pb-2">
              Converted page <span className="font-normal">(differences highlighted)</span>
            </div>
            {pageData ? (
              <iframe
                title={`Converted page ${page}`}
                sandbox=""
                className="w-full h-[75vh] border border-slate-200 bg-white"
                srcDoc={previewDoc(pageData.styles, pageData.preview_html)}
              />
            ) : (
              <div className="h-96 flex items-center justify-center text-sm text-slate-500" role="status">Loading…</div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function ItemList({
  data, items, onResult, onError,
}: {
  data: ReviewData;
  items: ReviewItem[];
  onResult: (d: ReviewData, message: string) => void;
  onError: (m: string) => void;
}) {
  if (!items.length) {
    return <p className="text-sm text-slate-600">No differences from the PDF on this page.</p>;
  }
  const open = items.filter((i) => i.status === "open");
  const done = items.filter((i) => i.status !== "open");
  return (
    <div className="card">
      <h3 className="px-4 pt-3 text-sm font-semibold text-ink">
        Differences on this page ({open.length} open{done.length ? `, ${done.length} resolved` : ""})
      </h3>
      <ul className="divide-y divide-slate-100">
        {[...open, ...done].map((it) => (
          <ItemRow key={it.id} data={data} item={it} onResult={onResult} onError={onError} />
        ))}
      </ul>
    </div>
  );
}

function ItemRow({
  data, item, onResult, onError,
}: {
  data: ReviewData;
  item: ReviewItem;
  onResult: (d: ReviewData, message: string) => void;
  onError: (m: string) => void;
}) {
  const [mode, setMode] = useState<"none" | "edit" | "accept">("none");
  const [text, setText] = useState(item.pdf_text || item.out_text);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const replaceable = !!item.out_text && item.page != null && !["missing", "extra", "picture_text", "added_label", "missing_picture"].includes(item.kind);
  const isOpen = item.status === "open";
  const base = `item-${item.id}`;

  async function run(action: "use_pdf" | "edit" | "accept") {
    setBusy(true);
    try {
      const d = await api.resolveItem(data.conversion.id, item.id, action, {
        text: action === "edit" ? text : undefined,
        note: note.trim() || undefined,
      });
      onResult(d, action === "accept" ? "Marked OK." : action === "use_pdf" ? "Replaced with the PDF's text." : "Text changed.");
      setMode("none");
    } catch (e) { onError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }

  return (
    <li className="px-4 py-3 text-sm" aria-labelledby={`${base}-label`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className={clsx("pill", !isOpen ? "bg-slate-100 text-slate-600" : item.severity === "blocking" ? "bg-rose-100 text-rose-800" : "bg-amber-50 text-amber-900")}>
              {!isOpen ? (item.severity === "blocking" ? "Was blocking" : "Was a warning") : item.severity === "blocking" ? "Must decide" : "Warning"}
            </span>
            <span className="font-medium text-ink" id={`${base}-label`}>{KIND_LABEL[item.kind]}</span>
            {item.page == null && <span className="text-xs text-slate-500">(whole document)</span>}
            {!isOpen && (
              <span className={item.status === "fixed" ? "pill-succeeded" : "pill-verified"}>
                {item.status === "fixed" ? "fixed" : "accepted"}
              </span>
            )}
          </div>
          <p className="mt-1 text-slate-700">{item.label}</p>
          {(item.pdf_text || item.out_text) && (
            <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs">
              {item.pdf_text && <><dt className="text-slate-500">PDF</dt><dd className="font-mono break-words">{item.pdf_text}</dd></>}
              {item.out_text && <><dt className="text-slate-500">Converted</dt><dd className="font-mono break-words">{item.out_text}</dd></>}
            </dl>
          )}
          {!isOpen && (item.note || item.resolved_by) && (
            <p className="mt-1 text-xs text-slate-500">
              {item.resolved_by && <>By {item.resolved_by}{item.resolved_at ? ` on ${new Date(item.resolved_at).toLocaleString()}` : ""}. </>}
              {item.note && <>Note: {item.note}</>}
            </p>
          )}
        </div>
        {isOpen && data.can_edit && (
          <div className="flex flex-wrap gap-1">
            {replaceable && item.pdf_text && (
              <button className="btn-secondary" disabled={busy} onClick={() => run("use_pdf")}>Use PDF text</button>
            )}
            {item.kind === "invented_sentence" && item.page != null && (
              <button className="btn-secondary" disabled={busy} onClick={() => run("use_pdf")}>Remove (not in the PDF)</button>
            )}
            {replaceable && (
              <button className="btn-ghost" disabled={busy} aria-expanded={mode === "edit"} onClick={() => setMode(mode === "edit" ? "none" : "edit")}>Edit…</button>
            )}
            <button className="btn-ghost" disabled={busy} aria-expanded={mode === "accept"} onClick={() => setMode(mode === "accept" ? "none" : "accept")}>Mark OK…</button>
          </div>
        )}
      </div>
      {mode === "edit" && (
        <form className="mt-2 flex flex-wrap items-end gap-2" onSubmit={(e) => { e.preventDefault(); run("edit"); }}>
          <label className="flex-1 min-w-[200px]">
            <span className="block text-xs text-slate-600 mb-1">Replace “{item.out_text}” with</span>
            <input className="input" value={text} onChange={(e) => setText(e.target.value)} required />
          </label>
          <button className="btn-primary" disabled={busy || !text.trim()}>Save</button>
        </form>
      )}
      {mode === "accept" && (
        <form className="mt-2 space-y-2" onSubmit={(e) => { e.preventDefault(); run("accept"); }}>
          <label className="block">
            <span className="block text-xs text-slate-600 mb-1">Why is this difference OK? (required, kept in the history)</span>
            <textarea className="input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} required minLength={3} />
          </label>
          <div className="flex flex-wrap gap-1 text-xs" aria-label="Common reasons">
            {NOTE_PRESETS[item.kind].map((p) => (
              <button key={p} type="button" className="btn-ghost px-2 py-1 text-xs" onClick={() => setNote(p)}>{p}</button>
            ))}
          </div>
          <button className="btn-primary" disabled={busy || note.trim().length < 3}>Mark OK</button>
        </form>
      )}
    </li>
  );
}

function PageEditor({
  data, page, onCancel, onSaved, onError,
}: {
  data: ReviewData;
  page: ReviewPage;
  onCancel: () => void;
  onSaved: (d: ReviewData) => void;
  onError: (m: string) => void;
}) {
  const [html, setHtml] = useState(page.html);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const area = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { area.current?.focus(); }, []);

  async function save() {
    setBusy(true);
    try { onSaved(await api.editPage(data.conversion.id, page.page, html, note.trim() || undefined)); }
    catch (e) { onError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }

  return (
    <form className="card card-body space-y-3" onSubmit={(e) => { e.preventDefault(); save(); }} aria-label={`Edit page ${page.page}`}>
      {data.edits_make_new_version && (
        <p className="text-sm text-amber-900 bg-amber-50 rounded p-2">
          Saving creates a new version for approval; the version visitors see is not changed.
        </p>
      )}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <label className="block">
          <span className="block text-xs font-semibold text-slate-600 mb-1">Page HTML</span>
          <textarea
            ref={area}
            className="input font-mono text-xs h-[60vh]"
            value={html}
            onChange={(e) => setHtml(e.target.value)}
            spellCheck={false}
          />
        </label>
        <div>
          <div className="text-xs font-semibold text-slate-600 mb-1">Preview</div>
          <iframe
            title={`Preview of edited page ${page.page}`}
            sandbox=""
            className="w-full h-[60vh] border border-slate-200 bg-white rounded"
            srcDoc={previewDoc(page.styles, html)}
          />
        </div>
      </div>
      <label className="block">
        <span className="block text-xs text-slate-600 mb-1">What did you change? (optional, kept in the history)</span>
        <input className="input" value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
      <div className="flex justify-end gap-2">
        <button type="button" className="btn-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
        <button type="submit" className="btn-primary" disabled={busy || html === page.html}>
          {busy && <span className="spinner" aria-hidden="true" />} Save page
        </button>
      </div>
    </form>
  );
}

function RedoDialog({
  data, initial, flagged, onClose, onStarted,
}: {
  data: ReviewData;
  initial: number[];
  flagged: number[];
  onClose: () => void;
  onStarted: (newId: string) => void;
}) {
  const [text, setText] = useState(initial.join(", "));
  const [est, setEst] = useState<RedoEstimate | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const pages = parsePages(text);
  const tooMany = pages.length > data.max_redo_pages;

  // A changed selection needs a fresh estimate before it can run.
  useEffect(() => { setEst(null); }, [text]);

  async function estimate() {
    setBusy(true); setError(null);
    try { setEst(await api.estimateRedo(data.conversion.id, pages)); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }

  async function run() {
    if (!est) return;
    setBusy(true); setError(null);
    try { onStarted((await api.redoPages(data.conversion.id, est.pages, est.estimate_usd)).id); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); setBusy(false); }
  }

  const model = data.redo_model.split("/").pop();
  return (
    <Modal
      open
      onClose={onClose}
      title="Redo pages with the stronger model"
      size="lg"
      footer={<>
        <button className="btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
        {!est ? (
          <button className="btn-secondary" onClick={estimate} disabled={busy || !pages.length || tooMany}>
            {busy && <span className="spinner" aria-hidden="true" />} Show cost
          </button>
        ) : (
          <button className="btn-primary" onClick={run} disabled={busy}>
            {busy && <span className="spinner" aria-hidden="true" />}
            Redo {est.pages.length} page{est.pages.length === 1 ? "" : "s"} for about ${est.estimate_usd.toFixed(2)}
          </button>
        )}
      </>}
    >
      <div className="space-y-3 text-sm">
        <p className="text-slate-700">
          The chosen pages are read again by {model}, with the PDF's own text, then checked against the PDF.
          The result is a new version for approval; other pages and the live version stay as they are.
        </p>
        <label className="block">
          <span className="block text-xs font-semibold text-slate-600 mb-1">Pages (e.g. 3, 7-9) — at most {data.max_redo_pages}</span>
          <input className="input" value={text} onChange={(e) => setText(e.target.value)} aria-invalid={tooMany} />
        </label>
        {flagged.length > 0 && (
          <button type="button" className="btn-ghost -ml-3" onClick={() => setText(flagged.slice(0, data.max_redo_pages).join(", "))}>
            Use the pages that need a decision ({flagged.length})
          </button>
        )}
        {tooMany && <p className="text-rose-700" role="alert">Choose at most {data.max_redo_pages} pages at a time.</p>}
        {est && (
          <div className="rounded-md border border-brand-200 bg-brand-50 p-3" role="status">
            <div className="font-semibold text-ink">
              About ${est.estimate_usd.toFixed(2)} for {est.pages.length} page{est.pages.length === 1 ? "" : "s"} (up to ${est.max_usd.toFixed(2)})
            </div>
            <div className="text-xs text-slate-600 mt-1">
              Pages {est.pages.join(", ")} · ~{est.tokens_input.toLocaleString()} input and ~{est.tokens_output.toLocaleString()} output tokens on {model}.
            </div>
          </div>
        )}
        {error && <p className="text-rose-700" role="alert">{error}</p>}
      </div>
    </Modal>
  );
}

const ACTION_LABEL: Record<string, string> = {
  use_pdf: "Used the PDF's text",
  edit_text: "Changed text",
  accept: "Marked OK",
  edit_page: "Edited the page",
  redo_pages: "Pages read again",
  approve: "Approved and published",
  unpublish: "Unpublished",
  new_version: "New version",
};

function HistoryDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const [rows, setRows] = useState<VersionHistoryEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api.getHistory(id).then(setRows).catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [id]);
  return (
    <Modal open onClose={onClose} title="Version history" size="lg">
      {error && <p className="text-rose-700" role="alert">{error}</p>}
      {!rows && !error && <p className="text-slate-500" role="status">Loading…</p>}
      {rows && (
        <ol className="space-y-4 text-sm">
          {rows.map((v) => (
            <li key={v.conversion_id} className="border-l-2 border-slate-200 pl-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold">Version {v.version}</span>
                {v.live && <span className="pill-succeeded">live</span>}
                {v.status === "failed" && <span className="pill-failed">failed</span>}
                {v.status === "running" && <span className="pill-running">running</span>}
                <span className="text-slate-500">{v.created_at ? new Date(v.created_at).toLocaleString() : ""}</span>
              </div>
              {v.change_summary && (
                <div className="text-slate-700">
                  {v.derived_from_version ? `From version ${v.derived_from_version}: ` : ""}{v.change_summary}
                </div>
              )}
              {v.approved_by && (
                <div className="text-emerald-800 text-xs">Approved by {v.approved_by}{v.approved_at ? ` on ${new Date(v.approved_at).toLocaleString()}` : ""}</div>
              )}
              {v.changes.length > 0 && (
                <ul className="mt-1 space-y-1">
                  {v.changes.map((ch) => (
                    <li key={ch.id} className="text-xs text-slate-700">
                      <span className="font-medium">{ACTION_LABEL[ch.action] ?? ch.action}</span>
                      {ch.page != null && <> on page {ch.page}</>}
                      {" — "}{ch.user ?? "system"}{ch.at ? `, ${new Date(ch.at).toLocaleString()}` : ""}
                      {(ch.action === "use_pdf" || ch.action === "edit_text") && (
                        <> : “{ch.before}” → “{ch.after}”</>
                      )}
                      {ch.note && <div className="text-slate-500">{ch.note}</div>}
                      {(ch.action === "edit_page" || ch.action === "redo_pages") && ch.before && (
                        <details className="mt-0.5">
                          <summary className="cursor-pointer text-brand-700">Before and after</summary>
                          <div className="grid grid-cols-2 gap-2 mt-1">
                            <pre className="whitespace-pre-wrap break-words bg-slate-50 p-2 max-h-48 overflow-auto">{ch.before}</pre>
                            <pre className="whitespace-pre-wrap break-words bg-slate-50 p-2 max-h-48 overflow-auto">{ch.after}</pre>
                          </div>
                        </details>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {v.error && <div className="text-rose-700 text-xs">{v.error}</div>}
            </li>
          ))}
        </ol>
      )}
    </Modal>
  );
}
